import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { Hero } from '@/components/public/Hero';
import { BlockRenderer, IconByName } from '@/components/public/Blocks';
import { GalleryCarousel } from '@/components/public/GalleryCarousel';
import { ReviewsSection } from '@/components/public/Reviews';
import { LocationSection } from '@/components/public/LocationSection';
import { Reveal, SectionHeading } from '@/components/public/Motion';
import { Img } from '@/components/public/Img';
import { FaqSection } from '@/components/public/Faq';
import { Seo } from '@/components/public/Seo';
import { ROUTE_META, reviewStats, resortSchema, faqSchema } from '@/lib/seo';
export default function HomePage() {
  const { site } = useSite();
  const page = site?.pages?.home || {};
  const experiences = site?.experiences || []; // same list editors manage under Experience
  const gallery = site?.gallery || [];
  const posts = (site?.posts || []).slice(0, 3);
  const stats = reviewStats(site?.reviews || []);
  const faqs = site?.settings?.faqs || [];

  return (
    <>
      <Seo
        path="/"
        title={ROUTE_META['/'].title}
        description={ROUTE_META['/'].description}
        jsonLd={[
          // aggregateRating is only emitted when it is exactly what the page shows (hero badge + reviews section)
          resortSchema(site, { withRating: stats.count > 0 ? stats : null }),
          faqs.length ? faqSchema(faqs) : null,
        ]}
      />
      <Hero />
      <BlockRenderer blocks={page.blocks || []} />

      {experiences.length > 0 && (
        <section className="section-y pt-0" data-testid="home-experience-section">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <SectionHeading label="Experience" title="The island, unhurried" />
              <Reveal><Link to="/experience" className="inline-flex items-center gap-2 eyebrow link-underline" aria-label="See all island experiences in Nusa Lembongan" data-testid="home-experience-link">All experiences <ArrowRight className="h-3.5 w-3.5" /></Link></Reveal>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-12">
              {experiences.map((e, i) => (
                <Reveal key={e.id} delay={i * 0.08} className="group rounded-xl border border-border bg-card p-7 shadow-[var(--shadow-soft)] transition-shadow duration-500 hover:shadow-[var(--shadow-float)]">
                  {e.image ? (
                    <div className="overflow-hidden rounded-lg mb-6 aspect-[4/3] bg-muted"><Img src={e.image} alt={e.image_alt || `${e.title} — Nusa Lembongan experience with Bukit Ancak`} widths={[480, 800]} sizes="(min-width: 640px) 33vw, 100vw" width={800} height={600} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /></div>
                  ) : (
                    <IconByName name={e.icon} className="h-6 w-6 text-[hsl(var(--brand-teal))] mb-6" />
                  )}
                  <h3 className="font-display text-xl">{e.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/70">{e.description}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {gallery.length > 0 && (
        <section className="section-y bg-card border-y border-border" data-testid="home-gallery-section">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-2">
              <SectionHeading label="Gallery" title="Moments at Bukit Ancak" />
              <Reveal><Link to="/gallery" className="inline-flex items-center gap-2 eyebrow link-underline" aria-label="View the Bukit Ancak photo gallery" data-testid="home-gallery-link">View gallery <ArrowRight className="h-3.5 w-3.5" /></Link></Reveal>
            </div>
            <GalleryCarousel items={gallery.slice(0, 10).map((g) => ({ ...g, alt: g.alt || g.caption || `${g.category || 'Resort'} — Bukit Ancak Lembongan, Nusa Lembongan` }))} />
          </div>
        </section>
      )}

      <ReviewsSection reviews={site?.reviews || []} />

      {posts.length > 0 && (
        <section className="section-y" data-testid="home-journal-section">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <SectionHeading label="Journal" title="Notes from the hill" />
              <Reveal><Link to="/journal" className="inline-flex items-center gap-2 eyebrow link-underline" aria-label="Read the Bukit Ancak journal: Nusa Lembongan travel stories" data-testid="home-journal-link">Read the journal <ArrowRight className="h-3.5 w-3.5" /></Link></Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <Link to={`/journal/${p.slug}`} className="group block" data-testid="home-journal-card">
                    <div className="overflow-hidden rounded-xl aspect-[4/3] bg-muted">{p.cover && <Img src={p.cover} alt={p.cover_alt || p.title} widths={[480, 800]} sizes="(min-width: 768px) 33vw, 100vw" width={800} height={600} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />}</div>
                    <p className="eyebrow text-muted-foreground mt-5">{p.category}{p.date ? ` · ${new Date(p.date + 'T00:00:00').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}` : ''}</p>
                    <h3 className="font-display text-xl mt-2 group-hover:text-[hsl(var(--brand-teal))] transition-colors">{p.title}</h3>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <FaqSection />
      <LocationSection />
    </>
  );
}
